import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useAsync } from 'react-use';
import _pickBy from 'lodash/pickBy';
import { getStoreLocation } from 'ck-utils';
import { Button, Loading } from 'ck-ui';
import { getSanityImg, sanityClient } from 'src/sanityClient';
import { Hero } from '../Hero/Hero';
import { HeroWithVideo } from '../Hero/HeroWithVideo';
import { ContentBlock } from '../ContentBlock/ContentBlock';
import { Faq } from '../Faq/Faq';
import { Contact } from '../Contact/Contact';
import { FlexGrid } from '../layout/FlexGrid';
import { Grid } from '../layout/Grid';
import { Consent } from '../Consent/Consent';
import { LandingSignUp } from '../LandingSignUp/LandingSignUp';
import { AppDownload } from '../AppDownload/AppDownload';
import { StickyNavigation } from '../FeatureSection/StickyNavigation';
import { FeatureCard } from '../FeatureSection/FeatureCard';
import { FeatureContainer } from '../FeatureSection/FeatureContainer';
import { CareerBlock } from '../CareerBlock/CareerBlock';
import { FeatureCollection } from '../../FeatureCollection/FeatureCollection';
import { PinCodePopup } from '../../NewsLetter/PinCodePopup';
import { HallOfFame } from '../HallOfFame/HallOfFame';
import { PortableText } from '../PortableText/PortableText';
import { DoubleHeroBanner } from '../DoubleHeroBanner/DoubleHeroBanner';
import { LazyLoadRecommendation } from '../../ProductListingPage/productList/productRecommendation/LazyLoadRecommendation';
import styles from './LandingPage.module.scss';
import './section.global.scss';

const LandingPage = ({ handle, title }) => {
  const landingRefs = useRef({});
  const state = useAsync(async () => {
    const storeLocation = getStoreLocation();
    const landingContent = await sanityClient.fetch(`*[_type == "landingPage" && handle == "${handle}"]{
      hasContact,
      "content": ${['NZ', 'US', 'UK'].includes(storeLocation) ? `${storeLocation.toLowerCase()}BlockContent` : 'auBlockContent'}[] {
       "blockHeroImage": blockHeroImage->{
          _id,
          "desktopImage": desktopImage.asset->{
            _id,
            "height": metadata.dimensions.height,
            "width": metadata.dimensions.width
          },
          "mobileImage": mobileImage.asset->{
            _id,
            "height": metadata.dimensions.height,
            "width": metadata.dimensions.width
          },
          link,
          title,
          trackingID,
          isFullScreen,
          "modal": modal->,
        },
        blockConsent->,
        blockHeroVideo->,
        blockChecklist->,
        blockHeroDoubleBanner->,
        blockFaq->,
        "blockGrid": blockGrid->{
          _id,
          header,
          backgroundColour,
          "flexBlock":flexBlock[]{
             _key,
            "desktopImage": desktopImage.asset->{
              _id,
              "height": metadata.dimensions.height,
              "width": metadata.dimensions.width
            },
            "mobileImage": mobileImage.asset->{
              _id,
              "height": metadata.dimensions.height,
              "width": metadata.dimensions.width
            },
            trackingID,
            title,
            url,
            content,
            buttonText
          }
        },
        "blockFlexGrid": blockFlexGrid->{
          _id,
          buttonText,
          buttonUrl,
          mobileColumns,
          desktopColumns,
          header,
          isScrollable,
          zoomed,
          backgroundColour,
          "flexBlock":flexBlock[]{
             _key,
            "image": image.asset->{
              _id,
              "height": metadata.dimensions.height,
              "width": metadata.dimensions.width
            },
            imageTitle,
            header,
            url,
            imageContent,
            trackingID,
            "modal":modal->,
          }
        },
        blockNavigation->,
        blockSignup->,
        blockAppDownload->,
        blockAlgoliaRecommendation->,
        blockFeatureCollection->,
        blockCareer->,
        blockHtmlContent->,
        blockPinCodePopup->,
        "blockHallOfFame": blockHallOfFame->{
          _id,
          heading,
          desc,
          "celebrities": celebrities[] {
            celebrity->{
              _id,
              name,
              desc,
              btnLink,
              btnText,
              trackingID,
              "image": image.asset->{
                _id,
                "height": metadata.dimensions.height,
                "width": metadata.dimensions.width
              },
              "events": events[] {
                "store": location->name,
                desc,
                btnLink,
                date,
                btnText,
                trackingID,
                "image": image.asset->{
                  _id,
                  "height": metadata.dimensions.height,
                  "width": metadata.dimensions.width
                },
                _key
             },
            }
          }
        },
        "blockBrandGrid":blockBrandGrid->{
          _id,
          header,
          url,
          "brands": vendors[]{
            "brand":vendor->title,
            href,
            _key,
          }
        }
      }
    }[0]`);

    return {
      hasContact: landingContent.hasContact || false,
      content: landingContent?.content?.map((obj) => _pickBy(obj, (value) => !(value === null || value === undefined || value._id === null))) || []
    };
  }, [handle]);

  if (!state || !state.value || state.error) return <Loading isLoading />;

  return (
    <>
      <div style={{ position: 'absolute' }}>
        <h1 style={{ position: 'absolute', left: '-9999px' }}>{title}</h1>
      </div>
      {state.value.content?.map((block, index) => {
        if (!block) return null;
        if (block.blockHeroImage) {
          return (
            <Hero
              key={block.blockHeroImage._id}
              blockHeroImage={block.blockHeroImage}
              handle={handle}
              position={index}
            />
          );
        }
        if (block.blockChecklist) {
          return (
            <section
              // eslint-disable-next-line no-return-assign
              ref={block.blockChecklist.anchorLink ? ele => landingRefs.current[`C-${block.blockChecklist.anchorLink}`] = ele
                : null}
              id={block.blockChecklist.anchorLink}
              key={block.blockChecklist._id}
            >
              <ContentBlock
                className={classnames(styles.contentBlock, { [styles.noMargin]: block.blockChecklist.noMargin })}
                blockChecklist={block.blockChecklist}
                position={index}
                handle={handle}
              />
            </section>

          );
        }
        if (block.blockFlexGrid) {
          const {
            _id,
            backgroundColour,
            header,
            isScrollable,
            mobileColumns,
            desktopColumns,
            flexBlock,
            zoomed,
            trackingID,
            buttonUrl,
            buttonText } = block.blockFlexGrid;
          return (
            <section
              key={_id}
              className={classnames('home-section', { 'home-section-hasbackground': backgroundColour })}
              style={{
                backgroundColor: backgroundColour || '#fff',
                marginBottom: index === state.value.content.length - 1 ? '60px' : undefined
              }}
            >
              <FlexGrid
                title={header}
                isScrollable={isScrollable && (!mobileColumns || !desktopColumns)}
                flexBlock={flexBlock}
                zoomed={zoomed && (!mobileColumns || !desktopColumns)}
                trackingID={trackingID}
                handle={handle}
                position={index}
                mobileColumns={mobileColumns}
                desktopColumns={desktopColumns}
              />
              {buttonUrl && (
                <Button size="large" className="home-grid-shop-all" renderAs="a" href={buttonUrl}>{buttonText || 'Shop All'}</Button>
              )}
            </section>
          );
        }
        if (block.blockConsent) {
          return (
            <Consent
              key={block.blockConsent._id}
              blockConsent={block.blockConsent}
              position={index}
              handle={handle}
            />
          );
        }
        if (block.blockSignup) {
          return (
            <LandingSignUp
              key={block.blockSignup._id}
              successMessage={block.blockSignup?.successMessage}
              source={block.blockSignup?.source}
            />
          );
        }
        if (block.blockGrid) {
          return (
            <Grid
              key={block.blockGrid._id}
              blockGrid={block.blockGrid}
              handle={handle}
              position={index}
            />
          );
        }
        if (block.blockBrandGrid) {
          return (
            <section
              key={block.blockBrandGrid._id}
              className="landing-layout-container home-section home-section-hasbackground"
              style={{ backgroundColor: '#f2f2f2' }}
            >
              <FlexGrid
                title={block.blockBrandGrid.header}
                brands={block.blockBrandGrid.brands}
                isMultiBrand
                handle={handle}
                position={index}
              />
              <Button size="large" className="home-grid-shop-all" renderAs="a" href={block.blockBrandGrid.url || '/collections/brands'}>SHOP ALL BRANDS</Button>
            </section>
          );
        }
        if (block.blockFaq) {
          return (
            <Faq key={block.blockFaq._id} blockFaq={block.blockFaq} />
          );
        }
        if (block.blockHeroVideo) {
          return (
            <HeroWithVideo
              key={block.blockHeroVideo._id}
              blockHeroVideo={block.blockHeroVideo}
              handle={handle}
              position={index}
            />
          );
        }
        if (block.blockAppDownload) {
          return (
            <AppDownload
              key={block.blockAppDownload._id}
              blockAppDownload={block.blockAppDownload}
              handle={handle}
              position={index}
            />
          );
        }
        if (block.blockAlgoliaRecommendation) {
          return (
            <section className="home-section">
              <div className="container is-clipped">
                <LazyLoadRecommendation
                  key={block.blockAlgoliaRecommendation._id}
                  model="trending-items"
                  heading={block.blockAlgoliaRecommendation.heading}
                  handles={block.blockAlgoliaRecommendation.handles}
                />
              </div>
            </section>
          );
        }
        if (block.blockNavigation) {
          const anchorLinks = block.blockNavigation.navigation?.map((item) => item.anchorID);
          const navTitles = block.blockNavigation.navigation?.map((item) => item.navTitle);
          return (
            <section key={block.blockNavigation._id}>
              <FeatureContainer
                blockNavigation={block.blockNavigation}
                handle={handle}
                position={index}
              >
                <div
                  className={classnames(styles.featureCards, styles.featureCardAlignment)}
                >
                  {block.blockNavigation.navigation?.map((item) => (
                    <FeatureCard
                      key={item._key}
                      anchorLink={item.anchorID}
                      centerComponent={item.centerComponent}
                      titleOnly={item.titleOnly}
                      description={item.description}
                      image={item.image}
                      textColour={item.textColor}
                      titleDesktop={item.titleDesktop}
                      backgroundColour={item.bgColor}
                    />
                  ))}
                </div>
              </FeatureContainer>
              { anchorLinks && (
                <StickyNavigation
                  key={block.blockNavigation._id}
                  anchorLink={anchorLinks}
                  linkName={navTitles}
                  landingRefs={landingRefs}
                />
              )}
            </section>
          );
        }
        if (block.blockFeatureCollection) {
          return (
            <section className="home-section" key={block.blockFeatureCollection._id}>
              <FeatureCollection
                boostUrl={block.blockFeatureCollection.boostUrl}
                handle={block.blockFeatureCollection.handle}
                enableCarousel={block.blockFeatureCollection.enableCarousel}
                hitsPerPage={block.blockFeatureCollection.hitsPerPage}
                title={block.blockFeatureCollection.header}
              />
            </section>
          );
        }
        if (block.blockCareer) {
          return (
            <CareerBlock pageSize={block.blockCareer.pageSize} key={block.blockCareer._id} />
          );
        }
        if (block.blockHtmlContent) {
          const { _id, portableContent, textAlignCenter } = block.blockHtmlContent;
          return (
            <section key={_id} className="home-section">
              <div className="container">
                <div className="columns">
                  <div className="column">
                    <PortableText
                      className={classnames('page-block', { 'has-text-centered': textAlignCenter })}
                      content={portableContent}
                    />
                  </div>
                </div>
              </div>
            </section>
          );
        }
        if (block.blockPinCodePopup) {
          return (
            <PinCodePopup
              pinCode={block.blockPinCodePopup.pinCode}
              heroImage={getSanityImg(block.blockPinCodePopup.heroImage).width(600).url()}
              contentImage={getSanityImg(block.blockPinCodePopup.contentImage).width(600).url()}
              popupHeading={block.blockPinCodePopup.popupHeading}
              successHeading={block.blockPinCodePopup.successHeading}
              postSignUpHeading={block.blockPinCodePopup.postSignUpHeading}
              urlContains={block.blockPinCodePopup.urlContains}
            />
          );
        }
        if (block.blockHallOfFame) {
          return (
            <HallOfFame
              key={block.blockHallOfFame._id}
              blockHallOfFame={block.blockHallOfFame}
              handle={handle}
              position={index}
            />
          );
        }
        if (block.blockHeroDoubleBanner) {
          return (
            <DoubleHeroBanner
              key={block.blockHeroDoubleBanner._id}
              blockDoubleHeroBanner={block.blockHeroDoubleBanner}
              handle={handle}
              position={index}
            />
          );
        }
        return null;
      })}
      {state.value.hasContact && <Contact />}
    </>
  );
};

LandingPage.propTypes = {
  handle: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

export { LandingPage };
